/**
 * Awards Carousel - Smooth infinite scroll carousel with drag support
 * Uses CSS animation for buttery smooth continuous scrolling
 */
(function () {
    function init(root) {
        if (!root || root.__gaInit) return;
        root.__gaInit = true;

        const viewport = root.querySelector('.globber-awards__viewport');
        const track = root.querySelector('.globber-awards__track');
        const originalItems = Array.from(track ? track.querySelectorAll('.award') : []);
        const prevBtn = root.querySelector('.globber-awards__btn--prev');
        const nextBtn = root.querySelector('.globber-awards__btn--next');

        if (!viewport || !track || originalItems.length === 0) return;

        // Clone items for seamless infinite loop
        const cloneItems = () => {
            // Clone all items and append to track for seamless loop
            originalItems.forEach(item => {
                const clone = item.cloneNode(true);
                clone.setAttribute('aria-hidden', 'true');
                clone.classList.add('award--clone');
                track.appendChild(clone);
            });
        };
        cloneItems();

        const allItems = Array.from(track.querySelectorAll('.award'));

        const state = {
            step: 0,
            originalWidth: 0, // Width of original items (before clones)
            scrollSpeed: 0.5, // Pixels per frame
            animationId: null,
            paused: false,
            resumeTimer: null,
            resumeDelayMs: 3000,
            dragging: false,
            dragStartX: 0,
            dragStartScroll: 0
        };

        function measureStep() {
            if (originalItems.length < 2) return originalItems[0].getBoundingClientRect().width;
            const a = originalItems[0].getBoundingClientRect();
            const b = originalItems[1].getBoundingClientRect();
            const step = (b.left - a.left);
            return Math.round(step || a.width);
        }

        function measureOriginalWidth() {
            // Measure total width of original items
            let width = 0;
            originalItems.forEach(item => {
                width += item.getBoundingClientRect().width;
            });
            // Add gaps
            const gap = state.step - originalItems[0].getBoundingClientRect().width;
            width += gap * originalItems.length;
            return width;
        }

        // Smooth continuous autoplay using requestAnimationFrame
        function animate() {
            if (state.paused || state.dragging) {
                state.animationId = requestAnimationFrame(animate);
                return;
            }

            viewport.scrollLeft += state.scrollSpeed;

            // Seamless loop: when we've scrolled past original items, reset
            if (viewport.scrollLeft >= state.originalWidth) {
                viewport.scrollLeft = viewport.scrollLeft - state.originalWidth;
            }

            state.animationId = requestAnimationFrame(animate);
        }

        function startAnimation() {
            if (state.animationId) return;
            state.animationId = requestAnimationFrame(animate);
        }

        function stopAnimation() {
            if (state.animationId) {
                cancelAnimationFrame(state.animationId);
                state.animationId = null;
            }
        }

        function pause() {
            state.paused = true;
        }

        function resume() {
            state.paused = false;
        }

        function resumeSoon() {
            clearTimeout(state.resumeTimer);
            state.resumeTimer = setTimeout(() => {
                resume();
            }, state.resumeDelayMs);
        }

        function userInteracted() {
            pause();
            resumeSoon();
        }

        // Seamless position normalization
        function normalizePosition() {
            if (viewport.scrollLeft >= state.originalWidth) {
                viewport.scrollLeft = viewport.scrollLeft - state.originalWidth;
            } else if (viewport.scrollLeft < 0) {
                viewport.scrollLeft = state.originalWidth + viewport.scrollLeft;
            }
        }

        // Button navigation - smooth scroll by one item
        if (nextBtn) {
            nextBtn.addEventListener('click', () => {
                userInteracted();
                const targetScroll = viewport.scrollLeft + state.step;
                smoothScrollTo(targetScroll);
            });
        }

        if (prevBtn) {
            prevBtn.addEventListener('click', () => {
                userInteracted();
                let targetScroll = viewport.scrollLeft - state.step;
                if (targetScroll < 0) {
                    // Jump to end seamlessly then scroll
                    viewport.scrollLeft = state.originalWidth + targetScroll;
                } else {
                    smoothScrollTo(targetScroll);
                }
            });
        }

        // Smooth scroll helper
        function smoothScrollTo(target) {
            const start = viewport.scrollLeft;
            const distance = target - start;
            const duration = 300;
            let startTime = null;

            function step(timestamp) {
                if (!startTime) startTime = timestamp;
                const elapsed = timestamp - startTime;
                const progress = Math.min(elapsed / duration, 1);
                
                // Easing function (ease-out)
                const eased = 1 - Math.pow(1 - progress, 3);
                
                viewport.scrollLeft = start + (distance * eased);

                if (progress < 1) {
                    requestAnimationFrame(step);
                } else {
                    normalizePosition();
                }
            }

            requestAnimationFrame(step);
        }

        // Drag/Swipe
        function onPointerDown(e) {
            if (e.pointerType === 'mouse' && e.button !== 0) return;

            userInteracted();

            state.dragging = true;
            state.dragStartX = e.clientX;
            state.dragStartScroll = viewport.scrollLeft;

            viewport.classList.add('is-dragging');

            try { viewport.setPointerCapture(e.pointerId); } catch (_) {}
        }

        function onPointerMove(e) {
            if (!state.dragging) return;
            const dx = e.clientX - state.dragStartX;
            viewport.scrollLeft = state.dragStartScroll - dx;
        }

        function onPointerUp(e) {
            if (!state.dragging) return;
            state.dragging = false;

            viewport.classList.remove('is-dragging');
            try { viewport.releasePointerCapture(e.pointerId); } catch (_) {}

            normalizePosition();
            snapToNearest();
        }

        function snapToNearest() {
            if (!state.step) return;
            const nearest = Math.round(viewport.scrollLeft / state.step);
            const target = nearest * state.step;
            smoothScrollTo(target);
        }

        viewport.addEventListener('pointerdown', onPointerDown, { passive: true });
        viewport.addEventListener('pointermove', onPointerMove, { passive: true });
        viewport.addEventListener('pointerup', onPointerUp, { passive: true });
        viewport.addEventListener('pointercancel', onPointerUp, { passive: true });
        viewport.addEventListener('pointerleave', (e) => {
            if (state.dragging && e.pointerType === 'mouse') onPointerUp(e);
        }, { passive: true });

        // Pause on hover/focus
        viewport.addEventListener('mouseenter', () => pause(), { passive: true });
        viewport.addEventListener('mouseleave', () => { resume(); }, { passive: true });
        viewport.addEventListener('focusin', () => pause(), { passive: true });
        viewport.addEventListener('focusout', () => { resume(); }, { passive: true });

        // Init layout
        function relayout() {
            state.step = measureStep();
            state.originalWidth = measureOriginalWidth();

            viewport.scrollTo({ left: 0, behavior: 'auto' });
            state.paused = false;
            startAnimation();
        }

        // Wait for images to load for accurate measurements
        const images = track.querySelectorAll('img');
        let loadedCount = 0;
        const totalImages = images.length;

        function checkReady() {
            loadedCount++;
            if (loadedCount >= totalImages || totalImages === 0) {
                requestAnimationFrame(relayout);
            }
        }

        if (totalImages === 0) {
            requestAnimationFrame(relayout);
        } else {
            images.forEach(img => {
                if (img.complete) {
                    checkReady();
                } else {
                    img.addEventListener('load', checkReady);
                    img.addEventListener('error', checkReady);
                }
            });
        }

        // Fallback in case load events don't fire
        setTimeout(relayout, 500);

        window.addEventListener('resize', () => {
            stopAnimation();
            requestAnimationFrame(() => {
                state.step = measureStep();
                state.originalWidth = measureOriginalWidth();
                normalizePosition();
                startAnimation();
            });
        });

        // Cleanup on page unload
        window.addEventListener('beforeunload', stopAnimation);
    }

    function boot() {
        document.querySelectorAll('.globber-awards').forEach(init);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot);
    } else {
        boot();
    }
})();
